import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { differenceInDays, differenceInMonths, format, isBefore } from 'date-fns';
import { Key, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import InputMask from '../components/InputMask';
import ToggleSwitch from '../components/ToggleSwitch';
import { AgreementType } from '../helpers/types';
import { useApplicationContext } from '../providers/ApplicationProvider';
import { useAuthContext } from '../providers/AuthProvider';

export const AgreementContainer = () => <Outlet />;

// Signeringer ligger nå i agreement.signings (avtalen kan signeres av flere).
// De gamle feltene applicant_signedby/applicant_signedon er utfaset og alltid
// tomme — utled status fra signings filtrert på signer_type === 'applicant'.
const getApplicantSignings = (a: AgreementType) =>
    (a.signings ?? []).filter(s => s.signer_type === 'applicant');

// Manuelt signert av bevilger utenfor Lyfti: avtalen er ferdig og skal vises
// som en signert avtale, men uten mulighet til å signere den digitalt.
const isManuallySigned = (a: AgreementType) => Boolean(a?.manually_signed);

const ApplicationAgreements = () => {
    const { t } = useTranslation();
    const { agreements } = useApplicationContext();

    return (
        <div className="row gx-5">
            <div className="col-lg-7">
                <h4 className="mb-4">{t('dashboard_agreements')}</h4>

                <div className="my-3 d-flex flex-column">
                    {agreements
                        ?.sort((a, b) => {
                            // Først: flytt alle usignerte (ingen applicant-signing) først
                            const aSigned = getApplicantSignings(a).length > 0 || isManuallySigned(a);
                            const bSigned = getApplicantSignings(b).length > 0 || isManuallySigned(b);

                            if (aSigned !== bSigned) {
                                // false (ikke signert) skal komme før true (signert)
                                return aSigned ? 1 : -1;
                            }

                            // Hvis begge er signert eller begge ikke er signert → sorter på deadline
                            return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
                        })
                        .map((agreement: AgreementType, index: Key) => (
                            <AgreementItem key={index} {...agreement} />
                        ))}
                </div>
            </div>
        </div>
    );
};

export const AgreementItem = (props: AgreementType) => {
    const { id, application, deadline, title, signings } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user: currentUser } = useAuthContext();

    const currentDate = new Date();
    const deadlineDate = new Date(deadline);

    const manuallySigned = isManuallySigned(props);
    const applicantSignings = getApplicantSignings(props);
    // Has the currently logged-in user signed?
    const currentUserSigning = applicantSignings.find(
        s => String(s.signer_id) === String(currentUser?.id)
    );
    // Has anyone signed?
    const anyoneSigned = applicantSignings.length > 0;
    // Manuelt signert avtale er ferdig — ingen kan signere den
    const closedForSigning = manuallySigned || Boolean(currentUserSigning);

    const deadlineStatus = (() => {
        if (manuallySigned) return 'signed';              // signed outside Lyfti
        if (currentUserSigning) return 'signed';          // this user signed
        if (anyoneSigned) return 'others_signed';         // others signed, this user hasn't
        if (isBefore(deadlineDate, currentDate)) return 'past';
        const monthsDifference = differenceInMonths(deadlineDate, currentDate);
        if (monthsDifference >= 1) return 'moreThanOneMonth';
        if (differenceInDays(deadlineDate, currentDate) <= 30) return 'withinOneMonth';
        return 'unknown';
    })();

    const cardClass = (() => {
        switch (deadlineStatus) {
            case 'past':
                return 'card card-alert mb-4 border-danger text-decoration-none report';
            case 'signed':
                return 'card mb-4 border text-decoration-none';
            case 'others_signed':
                return 'card card-warning mb-4 border-warning text-decoration-none report';
            default:
                return 'card card-warning mb-4 border-warning text-decoration-none report';
        }
    })();

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate(`/soknad/${application.id}/avtaler/${id}`);
    };

    return (
        <a href="#" className={cardClass} onClick={handleClick}>
            <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="mb-1">
                        {title}
                        {!anyoneSigned && !manuallySigned ? t('to_signing') : null}
                    </h5>
                    <p className="card-text text-start mb-0">
                        {manuallySigned ? (
                            <span>Manuelt signert</span>
                        ) : anyoneSigned ? (
                            <span className="d-flex flex-column gap-1">
                                {applicantSignings.map((s, i) => (
                                    <span key={i}>
                                        Signert {format(s.signed_at, 'dd.MM.yyyy')} kl {format(s.signed_at, 'HH:mm')} av {s.signer_name}
                                        {s.signer_role ? <span className="text-muted ms-1">({s.signer_role})</span> : null}
                                    </span>
                                ))}
                                {!closedForSigning && (
                                    <span className="text-primary fw-medium">+ Legg til signatur</span>
                                )}
                            </span>
                        ) : (
                            <span>#{application.id}</span>
                        )}
                    </p>
                </div>
                <div className="text-end d-flex flex-column">
                    {anyoneSigned || manuallySigned ? (
                        <>
                        <span className="fw-medium text-success">
                            {t('signed')} <FontAwesomeIcon icon={['far', 'circle-check']} className="ms-1" />
                        </span>
                        {!closedForSigning ? (
                        <span className={`fw-medium ${deadlineStatus === 'past' ? 'text-danger' : ''}`}>
                            {t('deadline')} {format(deadlineDate, 'dd.MM.yyyy')}
                        </span>
                        ) : null}
                        </>
                    ) : (
                        <span className={`fw-medium ${deadlineStatus === 'past' ? 'text-danger' : ''}`}>
                            {t('deadline')} {format(deadlineDate, 'dd.MM.yyyy')}
                        </span>
                    )}

                    <span className="card-link mt-1">
                        {closedForSigning ? t('see_agreement') : t('read_and_sign')}
                        <FontAwesomeIcon icon={['fal', 'arrow-right']} className="ms-1" />
                    </span>
                </div>
            </div>
        </a>
    );
};


export const Agreement = () => {
    const auth = useAuth();
    const { agreements, setAgreements, loadApplication } = useApplicationContext();

    const { apiCall, user } = useAuthContext();
    const params = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    if (!agreements) {
        navigate('/soknad/' + params.id);
        return;
    }

    const agreement: AgreementType = agreements?.filter((a) => a.id === parseInt(params.agreement ?? ''))[0];

    const [consent, setConsent] = useState<boolean>(false);
    const [birthNumber, setBirthNumber] = useState<string>('');
    const [role, setRole] = useState<string>('');
    const [signing, setSignging] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const applicantSignings = getApplicantSignings(agreement);
    const currentUserSigning = applicantSignings.find(
        s => String(s.signer_id) === String(user?.id)
    );
    // Manuelt signert: vis avtalen, men ikke signeringsskjemaet
    const manuallySigned = isManuallySigned(agreement);



    const signAgreement = async () => {
        setSignging(true);

        if (!auth?.user) return false;
        const user = auth.user;

        // check if nin is same as user logged in
        if (birthNumber !== user.profile.nin) {
            setError('Personnummer stemmer ikke overrens med hvem som er logget inn.');
            setSignging(false);
            return;
        }

        if (!auth.isAuthenticated) {
            await auth.signinPopup({
                extraQueryParams: { login_hint: `nin:${birthNumber}` },
            });
        }
      
        try {
            await apiCall({
                action: 'signAgreement',
                id: agreement.id,
                consent,
                birthNumber,
                role,
                provider_reference: user.session_state,
                signature_certificate: {
                    name: user.profile.name,
                    signed_nin: user.profile.sub,
                    id_token: user.id_token,
                    sid: user.profile.sid,
                    sub: user.profile.sub,
                    birthdate: user.profile.birthdate,
                    transaction_id: user.profile.transaction_id
                },
            }).then((data) => {
                const response = data.data;
                if (response.success) {
                    // Signeringer ligger nå i agreement.signings. Oppdater dem
                    // optimistisk fra responsen; loadApplication() henter uansett
                    // ferske data rett etter.
                    const updatedAgreements = agreements.map((item) =>
                        item.id === agreement.id
                            ? { ...item, signings: response.signings ?? item.signings }
                            : item);
                    setAgreements(updatedAgreements);

                    loadApplication();

                    navigate(`/soknad/${params.id}/avtaler`);
                } else {
                    setError(response.message);
                }
            });
        } catch (error) {
            console.error(error);
        } finally {
            setSignging(false);
        }
    };

    return (currentUserSigning || manuallySigned) ? (
    <>
        <div className="d-flex w-100 justify-content-between align-items-center">
            <div>
                <h4>{t('see_agreement')}</h4>
                {/* <p>
                    Avtalen ble signert: {format(currentUserSigning.signed_at, 'dd.MM.yyyy')} kl. {format(currentUserSigning.signed_at, 'HH:mm')}
                </p> */}
            </div>
            <a href={undefined} onClick={() => navigate(-1)} className="text-link text-decoration-none cursor-pointer">
                {t('close')}
            </a>
        </div>

        {manuallySigned && (
            <div className="alert alert-success mb-3">
                Denne avtalen er registrert som manuelt signert og krever ingen signering her.
            </div>
        )}

        {applicantSignings.length > 1 && (
            <div className="alert alert-success mb-3">
                {applicantSignings.map((s, i) => (
                    <div key={i}>
                        Signert av {s.signer_name} ({s.signer_role}) — {format(s.signed_at, 'dd.MM.yyyy')} kl. {format(s.signed_at, 'HH:mm')}
                    </div>
                ))}
            </div>
        )}

        <div className="">
            <FilePreview url={import.meta.env.VITE_API_BASE_URL + agreement.location} />
        </div>
    </>

    ) : (
        <div>
            <div className="d-flex w-100 justify-content-between align-items-center">
                <h4>{t('sign_agreement')}</h4>
                <a href={undefined} onClick={() => navigate(-1)} className="text-link text-decoration-none cursor-pointer">
                    {t('cancel')}
                </a>
            </div>

             {/* Show if others have already signed */}
            {applicantSignings.length > 0 && (
                <div className="alert alert-info my-3">
                    <p className="mb-1 fw-semibold">Allerede signert av:</p>
                    {applicantSignings.map((s, i) => (
                        <div key={i}>
                            {s.signer_name} ({s.signer_role}) — {format(s.signed_at, 'dd.MM.yyyy')}
                        </div>
                    ))}
                </div>
            )}

            <div className="alert alert-warning my-4">
                <h5 className="fw-semibold">{t('signatory')}</h5>
                <p>
                    {t('signatory_text_1')} {agreement.applicant.name}: <span className="fw-semibold">{agreement.signatory}. </span>
                    {t('signatory_text_2')}{' '}
                    <a href={`/soknad/${agreement.application.id}/tilgangsstyring`} className="text-body">
                        {t('dashboard_sharing_access')}.
                    </a>
                </p>
            </div>

            <div className="">
                <FilePreview url={import.meta.env.VITE_API_BASE_URL + agreement.location} />
            </div>

            <div className="row mt-4">
                <ToggleSwitch label={t('agreement_consent') + ' ' + agreement.signatory} description="" value={consent} onCheck={() => setConsent(!consent)} />
            </div>

            <div className="border rounded-1 text-center bg-body-tertiary mt-4">
                {error && <div className="text-danger mb-2 mt-4">{error}</div>}
                <div className="my-4 py-2 px-5">
                    <div className="row gx-5 align-items-end text-start">
                        <div className="col-auto">
                            <img src="/svg/bank-id-icon.svg" alt="BankID Logo" className="me-3" />
                        </div>
                        <div className="col">
                            <InputMask name="fodselsnummer" label="Fødselsnummer" type="inputmask" placeholder="Elleve siffer" value={birthNumber} onChange={setBirthNumber} error={birthNumber.length !== 11 ? 'hidden' : null} mask="000000 00000" replace=" " replaceWith="" required rules={{}} />
                        </div>
                        <Input name="organization_role" label="Rolle i organisasjonen" type="text" placeholder="Skriv inn din rolle" col="col" value={role} onChange={setRole} required rules={{}} error={role.length < 2 ? 'hidden' : null} />
                        <div className="col-auto text-center">
                            <Button title="Signer avtale" onClick={signAgreement} disabled={!consent || birthNumber.length !== 11 || role.length < 2} loading={signing} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FilePreview = memo(({ url }: { url: string }) => {
    if (!url) return <p>Ingen dokument er oppgitt</p>;

    return (
        <DocViewer
            documents={[{ uri: url }]}
            pluginRenderers={DocViewerRenderers}
            config={{
                header: {
                    disableFileName: true,
                },
                pdfZoom: {
                    zoomJump: 0.25,
                    defaultZoom: 0.7,
                },
            }}
            style={{ backgroundColor: '#f3f6fc' }}
            className="my-doc-viewer-style"
        />
    );
});

export default ApplicationAgreements;
